/**
 * Opens when a request hits a plan limit or runs out of credits (see
 * UPGRADE_NEEDED in lib/api), explains what happened and offers the plan that
 * actually lifts it. Mounted once at the app root.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { billingApi, planName, UPGRADE_NEEDED, type BillingStatus, type UpgradeNeededDetail } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Icon } from "@/components/ui/icon";
import { cn } from "@/lib/utils";

type PaidPlan = "starter" | "growth";

const HEADLINES: Record<string, string> = {
  OUT_OF_CREDITS: "You're out of credits for this month",
  UPGRADE_REQUIRED: "That's on the paid plans",
  DOMAIN_LIMIT_REACHED: "You've hit your competitor limit",
  URL_LIMIT_REACHED: "You've hit your page limit",
  SEAT_LIMIT_REACHED: "Your plan is out of seats",
  WORKSPACE_LIMIT_REACHED: "Need another workspace?",
};

// Free and Starter share the same competitor, page and workspace limits, so
// only Growth lifts those; credits, Mira's analysis and seats start at Starter.
const NEEDS_GROWTH = new Set(["DOMAIN_LIMIT_REACHED", "URL_LIMIT_REACHED", "WORKSPACE_LIMIT_REACHED"]);

const HIGHLIGHTS: Record<PaidPlan, { price: string; points: string[] }> = {
  starter: {
    price: "$49",
    points: ["2,000 credits a month", "Mira's competitor analysis", "SEO benchmarks", "Up to 3 seats"],
  },
  growth: {
    price: "$149",
    points: ["10,000 credits a month", "50 competitors, 60 pages", "Unlimited workspaces", "Teammates at $29 / seat"],
  },
};

export function UpgradeDialog() {
  const [detail, setDetail] = useState<UpgradeNeededDetail | null>(null);
  const [billing, setBilling] = useState<BillingStatus | null>(null);
  const [loadingPlan, setLoadingPlan] = useState<PaidPlan | null>(null);

  useEffect(() => {
    const onNeeded = (e: Event) => {
      const next = (e as CustomEvent<UpgradeNeededDetail>).detail;
      // Keep the first reason if several requests fail at once
      setDetail((open) => open ?? next);
      billingApi.get().then((r) => { if ("data" in r && r.data) setBilling(r.data); });
    };
    window.addEventListener(UPGRADE_NEEDED, onNeeded);
    return () => window.removeEventListener(UPGRADE_NEEDED, onNeeded);
  }, []);

  const close = () => { setDetail(null); setLoadingPlan(null); };

  const checkout = async (plan: PaidPlan) => {
    setLoadingPlan(plan);
    const res = await billingApi.checkout(plan);
    if ("data" in res && res.data?.url) {
      window.location.href = res.data.url;
    } else {
      toast.error(res.error?.message ?? "Could not start checkout. Please try again.");
      setLoadingPlan(null);
    }
  };

  const reason = detail?.reason ?? "";
  const current = billing?.plan ?? "trial";
  const isOwner = billing?.isOwner ?? true;
  // Plans above the current one; the one that lifts this limit is recommended
  const offers: PaidPlan[] = current === "growth" ? [] : current === "starter" ? ["growth"] : ["starter", "growth"];
  const recommended: PaidPlan = NEEDS_GROWTH.has(reason) || current === "starter" ? "growth" : "starter";

  return (
    <Dialog open={!!detail} onOpenChange={(open) => !open && close()}>
      <DialogContent className="max-w-xl gap-5">
        <DialogHeader className="space-y-2 text-left">
          <p className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground">
            {planName(current)} plan
          </p>
          <DialogTitle className="text-xl leading-tight">{HEADLINES[reason] ?? "Upgrade to keep going"}</DialogTitle>
          <DialogDescription className="font-sans text-xs leading-relaxed">{detail?.message}</DialogDescription>
        </DialogHeader>

        {!isOwner ? (
          <p className="rounded-lg border border-border bg-muted/40 px-4 py-3 font-sans text-xs text-muted-foreground">
            This workspace is on its owner's plan. Ask them to upgrade in Settings → Billing.
          </p>
        ) : offers.length === 0 ? (
          <p className="rounded-lg border border-border bg-muted/40 px-4 py-3 font-sans text-xs text-muted-foreground">
            You're on our largest plan. Need more room?{" "}
            <a href="mailto:hello@peersheep.com" className="text-foreground underline underline-offset-2">Talk to us</a>.
          </p>
        ) : (
          <div className={cn("grid gap-3", offers.length > 1 && "sm:grid-cols-2")}>
            {offers.map((plan) => {
              const isRecommended = plan === recommended;
              return (
                <div
                  key={plan}
                  className={cn(
                    "relative flex flex-col gap-4 rounded-lg border p-4",
                    isRecommended ? "border-foreground/40 bg-foreground/[0.03]" : "border-border",
                  )}
                >
                  {isRecommended && offers.length > 1 && (
                    <span className="absolute -top-2.5 left-4 rounded-full bg-foreground px-2 py-0.5 font-mono text-[11px] uppercase tracking-widest text-background">
                      Recommended
                    </span>
                  )}
                  <div>
                    <p className="font-mono text-[11px] font-semibold uppercase tracking-widest">{planName(plan)}</p>
                    <p className="mt-1">
                      <span className="text-2xl font-semibold">{HIGHLIGHTS[plan].price}</span>
                      <span className="font-sans text-xs text-muted-foreground"> / month</span>
                    </p>
                  </div>
                  <ul className="flex-1 space-y-1.5">
                    {HIGHLIGHTS[plan].points.map((point) => (
                      <li key={point} className="flex items-center gap-2 font-sans text-xs text-muted-foreground">
                        <Icon name="checkmark-circle-01" size={13} className="shrink-0 text-foreground" />
                        {point}
                      </li>
                    ))}
                  </ul>
                  <Button
                    size="sm"
                    variant={isRecommended ? "default" : "outline"}
                    disabled={loadingPlan !== null}
                    onClick={() => checkout(plan)}
                  >
                    {loadingPlan === plan && <Icon name="refresh" size={12} className="mr-2 animate-spin" />}
                    Upgrade to {planName(plan)}
                  </Button>
                </div>
              );
            })}
          </div>
        )}

        <div className="flex items-center justify-between gap-3">
          <Link
            to="/settings?tab=billing"
            onClick={close}
            className="font-mono text-[11px] uppercase tracking-widest text-muted-foreground transition-colors hover:text-foreground"
          >
            Compare plans
          </Link>
          <Button variant="ghost" size="sm" onClick={close}>Not now</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
